import { z } from 'zod';
import { AssistantError } from './assistant';
import { uuidV7Schema } from './ids';

const timestamp = z.iso.datetime({ offset: true });
const reference = z.string().trim().min(1).max(1000);
export const privacyRequestSchema = z
  .object({
    conversationId: uuidV7Schema,
    reason: z.enum([
      'erasure_request',
      'purpose_ended',
      'processor_contract_ended',
    ]),
    authorityReference: reference,
  })
  .strict();
export const taxRetentionSchema = z
  .object({
    filingDeadline: z.iso.date(),
    prescriptionEndsAt: timestamp,
    determinationReference: reference,
  })
  .strict();
export const disposalDecisionSchema = z
  .object({
    purposeEndedAt: timestamp,
    retainUntil: timestamp,
    basisReference: reference,
    financialClassification: z.enum([
      'not_financial',
      'tax_evidence',
      'other_financial',
    ]),
    tax: taxRetentionSchema.nullable(),
    processorContractEndedAt: timestamp.nullable(),
    controllerReturnReference: reference.nullable(),
    statutoryExceptionReference: reference.nullable(),
    downstreamInstructions: z
      .array(
        z
          .object({ recipient: reference, instructionReference: reference })
          .strict()
      )
      .max(50),
  })
  .strict()
  .superRefine((value, context) => {
    if (
      (value.financialClassification === 'tax_evidence') !==
      (value.tax !== null)
    )
      context.addIssue({
        code: 'custom',
        message:
          'Tax evidence requires its actual filing deadline and prescription determination.',
      });
    if (value.processorContractEndedAt && !value.controllerReturnReference)
      context.addIssue({
        code: 'custom',
        message: 'Confirm return to the controller before processor disposal.',
      });
  });
export type DisposalDecision = z.infer<typeof disposalDecisionSchema>;
export const legalHoldSchema = z
  .object({ id: uuidV7Schema, basisReference: reference })
  .strict();
export const releaseLegalHoldSchema = z
  .object({ releaseReference: reference })
  .strict();
export const privacyCaseSchema = z.object({
  id: uuidV7Schema,
  conversationId: uuidV7Schema,
  state: z.enum(['blocked', 'scheduled', 'deleting', 'complete']),
  reason: privacyRequestSchema.shape.reason,
  authorityReference: reference,
  requestedBy: uuidV7Schema,
  createdAt: timestamp,
  deleteAfter: timestamp.nullable(),
  completedAt: timestamp.nullable(),
  decision: disposalDecisionSchema.nullable(),
  completion: z
    .object({
      deletedObjects: z.number().int().nonnegative(),
      customerProfileCleared: z.boolean(),
      retainedUsageEvents: z.string().regex(/^\d+$/u),
      retainedInteractionWindows: z.string().regex(/^\d+$/u),
      auditTrailRetained: z.literal(true),
      providerOriginalsDeleted: z.literal(false),
    })
    .nullable(),
});

export const privacyExportSectionSchema = z.enum([
  'conversations',
  'messages',
  'channel_inbox',
  'assistant_actions',
  'channel_outbox',
  'classification_decisions',
  'classification_reviews',
  'conversation_attachments',
  'conversation_holds',
  'collection_followups',
  'usage_events',
  'model_operations',
  'client_memory_learning',
  'client_memory_turns',
  'interaction_windows',
  'action_executions',
]);
export const privacyExportPageSchema = z.object({
  section: privacyExportSectionSchema,
  records: z.array(
    z.object({
      id: z.string(),
      cursor: z.string(),
      payload: z.record(z.string(), z.unknown()),
    })
  ),
  nextCursor: z.string().nullable(),
});

export function taxEvidenceFloor(filingDeadline: string): string {
  const date = z.iso.date().parse(filingDeadline);
  // Art. 87.7: five years counted from 1 January after the filing-deadline year, in Peru's timezone.
  return `${Number(date.slice(0, 4)) + 6}-01-01T00:00:00-05:00`;
}

export function disposalDate(
  input: DisposalDecision,
  now = new Date()
): string {
  const decision = disposalDecisionSchema.parse(input);
  if (Date.parse(decision.purposeEndedAt) > now.getTime())
    throw new AssistantError('PRIVACY_PURPOSE_NOT_ENDED', 409);
  const dates = [decision.purposeEndedAt, decision.retainUntil];
  if (decision.tax)
    dates.push(
      taxEvidenceFloor(decision.tax.filingDeadline),
      decision.tax.prescriptionEndsAt
    );
  const date = new Date(Math.max(...dates.map(Date.parse)));
  if (decision.processorContractEndedAt) {
    const ended = new Date(decision.processorContractEndedAt);
    if (ended.getTime() > now.getTime())
      throw new AssistantError('PRIVACY_CONTRACT_NOT_ENDED', 409);
    const limit = new Date(ended);
    limit.setUTCFullYear(limit.getUTCFullYear() + 2);
    if (date > limit && !decision.statutoryExceptionReference)
      throw new AssistantError('PRIVACY_PROCESSOR_RETENTION_LIMIT', 409);
  }
  return date.toISOString();
}
